import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { format } from 'date-fns';
import { useFormik } from 'formik';

import { useNavigationStack } from '../../../context/NavigationStackContext';
import { useExerciseResultsForSetAutofill } from '../../../hooks/use-exercise-results-for-set-autofill';
import {
  isActivityTypeSubmitBlocked,
  resolveActivityTypeIdForSubmit,
  useTrainingActivityTypePicker,
  useTrainingActivityTypePreselect,
} from '../../../hooks/use-training-activity-type-picker';
import { useProgramItemExercises } from '../../../hooks/useProgramItemExercises';
import {
  useGetAvailableUserProgramItems,
  useGetProgramItems,
  useLazyGetProgramItemById,
} from '../../../redux/api/programsAPI';
import { usePostPutTraining } from '../../../redux/api/trainingsAPI';
import { useGetUserTrainingAccessIncoming } from '../../../redux/api/userTrainingAccessApi';
import { useGetMyDetails } from '../../../redux/api/users/usersApi';
import { TrainingSetItemStatus } from '../../../shared/constants';
import { IProgramSet } from '../../../types/interfaces/program-set';
import { ITraining } from '../../../types/interfaces/training';
import { IUser } from '../../../types/interfaces/user';
import { groupBy, orderBy } from '../../../utils/collections/array-utils';
import { getUserViewName } from '../../../utils/common/shared-util';
import { generateTemporaryId } from '../../../utils/training/set-utils';
import { accessRowToIUser } from '../../users/settings/access-settings/access-settings-helpers';
import { useEditTraining } from '../edit-training/useEditTraining';
import { getExerciseResultsUserIdForTrainingForm } from '../shared/training-owner-form';
import { getNewTrainingFormValidation } from './FormikData';

export function useNewTrainingPage() {
  const { t } = useTranslation();
  const { back } = useNavigationStack();
  const { data: programItems } = useGetProgramItems();
  const { data: userAvailableProgramItems } = useGetAvailableUserProgramItems();
  const [getProgramItem, { data: programItem }] = useLazyGetProgramItemById();

  const { data: user } = useGetMyDetails();
  const { data: userTrainingAccess } = useGetUserTrainingAccessIncoming();
  const [postPutTraining, { isLoading }] = usePostPutTraining();
  const { exercises } = useProgramItemExercises({
    programItemId: programItem?.id,
  });
  const defaultTrainingName = format(new Date(), 'dd.MM.yyyy');
  const [showSavedTrainingModal, setShowSavedTrainingModal] = useState(false);

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema: getNewTrainingFormValidation(t),
    initialValues: {
      title: '',
      description: '',
      programItem: null,
      trainingSets: [],
      planedStartDate: null,
      activityTypeId: null,
      createForOthers: false,
      trainingOwner: null,
    },
    onSubmit,
  });

  useTrainingActivityTypePreselect(formik);

  const {
    activityTypes,
    isLoading: isActivityTypesLoading,
    isError: isActivityTypesError,
    defaultActivityTypeId,
  } = useTrainingActivityTypePicker();

  const isSubmitDisabled = isActivityTypeSubmitBlocked(
    isActivityTypesLoading,
    activityTypes,
    formik.values.activityTypeId,
    defaultActivityTypeId,
    isActivityTypesError,
  );

  useEffect(() => {
    if (programItem && exercises) {
      const trainingSets = getTrainingSetsFromProgramSets(
        programItem.programSets,
      );

      formik.setFieldValue('trainingSets', trainingSets);
    }
  }, [programItem, exercises]);

  function onSubmit(
    values: ITraining & {
      createForOthers?: boolean;
      trainingOwner?: IUser | null;
    },
  ) {
    const { createForOthers, trainingOwner, ...rest } = values;
    const ownerId =
      createForOthers && trainingOwner?.id ? trainingOwner.id : user?.id;

    const body: ITraining = {
      ...rest,
      ownerId,
      activityTypeId: resolveActivityTypeIdForSubmit(
        values.activityTypeId,
        defaultActivityTypeId,
        activityTypes,
      ),
      trainingSets: values.trainingSets.map((set) => {
        const { exercise, ...other } = set;

        return other;
      }),
      title: values.title || defaultTrainingName,
    };

    postPutTraining({
      body,
    })
      .unwrap()
      .then(() => back())
      .catch((error) => console.log(error));
  }

  function getTrainingSetsFromProgramSets(programSets: IProgramSet[]) {
    const trainingSets = [];

    const groupedProgramSets = groupBy(
      orderBy(programSets, ['setNumber', 'orderNumber']),
      'setNumber',
    );
    Object.values(groupedProgramSets).forEach(
      (programSetsGroup: IProgramSet[]) => {
        orderBy(programSetsGroup, ['setNumber', 'orderNumber']).forEach(
          (programSet) => {
            let trainingSetItemOrderNumber = 0;
            trainingSets.push({
              exerciseId: programSet.exerciseId,
              exercise: exercises.find((e) => e.id === programSet.exerciseId),
              setNumber: programSet.setNumber,
              orderNumber: programSet.orderNumber,
              temporaryId: generateTemporaryId(),
              trainingSetItems: orderBy(
                programSet.programSetItems,
                'orderNumber',
              ).flatMap((programSetItem) => {
                const trainingSetItems = [];

                for (let i = 0; i < programSetItem.sets; i++) {
                  trainingSetItems.push({
                    orderNumber: trainingSetItemOrderNumber,
                    rest: programSetItem.rest,
                    type: programSetItem.type,
                    status: TrainingSetItemStatus.notStarted,
                    temporaryId: generateTemporaryId(),
                    trainingSetItemOptions:
                      programSetItem.programSetItemOptions.map(
                        (setItemOption) => ({
                          optionId: setItemOption.optionId,
                          value: null,
                          value2: null,
                          programValue: setItemOption.value,
                          programValue2: setItemOption.value2,
                        }),
                      ),
                  });
                  trainingSetItemOrderNumber++;
                }

                return trainingSetItems;
              }),
            });
          },
        );
      },
    );

    return trainingSets;
  }

  function onProgramItemChange(e) {
    if (e?.target?.value) {
      getProgramItem(e.target.value.id);
      formik.setFieldValue('programItemId', e.target.value.id);
      formik.setFieldValue('programItem', e.target.value);
    } else {
      formik.setFieldValue('trainingSets', []);
      formik.setFieldValue('programItemId', null);
      formik.setFieldValue('programItem', null);
    }
  }

  const createTrainingRecipients = userTrainingAccess?.createTraining ?? [];
  const canCreateForOthers = createTrainingRecipients.length > 0;

  const ownerSelectOptions = orderBy(
    createTrainingRecipients.map((row) => {
      const u = accessRowToIUser(row);
      return {
        ...u,
        displayName: getUserViewName(u),
      };
    }),
    ['displayName'],
    ['asc'],
  );

  /** Пока тоггл «для другого» выключен — пустой disabled-селект; в форме `trainingOwner` не заполняем. */
  const performerSelectOptions = useMemo(() => {
    if (!formik.values.createForOthers || !canCreateForOthers) {
      return [];
    }
    return ownerSelectOptions;
  }, [canCreateForOthers, formik.values.createForOthers, ownerSelectOptions]);

  const onCreateForOthersChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const checked = e.target.checked;
      formik.setFieldValue('createForOthers', checked);
      if (checked && ownerSelectOptions[0]) {
        formik.setFieldValue('trainingOwner', ownerSelectOptions[0]);
      } else {
        formik.setFieldValue('trainingOwner', null);
      }
    },
    [formik, ownerSelectOptions],
  );

  /** New training: owner/performer can always be changed when access allows. */
  const canChangeOwner = true;

  const exerciseResultsUserId = useMemo(
    () =>
      getExerciseResultsUserIdForTrainingForm(
        formik.values.trainingOwner,
        formik.values.createForOthers,
        user?.id,
      ),
    [formik.values.trainingOwner, formik.values.createForOthers, user?.id],
  );
  const exerciseResults = useExerciseResultsForSetAutofill(
    exerciseResultsUserId,
  );

  const userProgramItems = orderBy(
    [...(programItems || []), ...(userAvailableProgramItems || [])].filter(
      Boolean,
    ),
    'name',
  );

  const {
    onChangeTrainingSet,
    onDuplicateTrainingSet,
    onRemoveTrainingSet,
    getSetIndex,
    onAddEmptyTrainingSet,
    onAddTrainingSetFromSavedProgramSet,
    onChangeSetOrder,
  } = useEditTraining(formik);

  return {
    defaultTrainingName,
    formik,
    isLoading,
    isSubmitDisabled,
    canCreateForOthers,
    canChangeOwner,
    exerciseResults,
    user,
    performerSelectOptions,
    onCreateForOthersChange,
    userProgramItems,
    programItem,
    exercises,
    onChangeTrainingSet,
    onDuplicateTrainingSet,
    onRemoveTrainingSet,
    onProgramItemChange,
    onAddEmptyTrainingSet,
    onChangeSetOrder,
    onAddTrainingSetFromSavedProgramSet,
    getSetIndex,
    showSavedTrainingModal,
    setShowSavedTrainingModal,
  };
}
